import { useState } from "react";
import { usePause, useClearPause } from "../api/queries";
import type { OverrideState } from "../api/types";

const PRESETS: Array<{ label: string; seconds: number | null }> = [
  { label: "15 min", seconds: 15 * 60 },
  { label: "1 h", seconds: 60 * 60 },
  { label: "4 h", seconds: 4 * 60 * 60 },
  { label: "Until midnight", seconds: null /* computed below */ },
  { label: "Infinite", seconds: null },
];

function secondsUntilMidnight(): number {
  const now = new Date();
  const mid = new Date(now);
  mid.setHours(24, 0, 0, 0);
  return Math.max(60, Math.round((mid.getTime() - now.getTime()) / 1000));
}

interface Props {
  override: OverrideState | null;
}

export default function PauseControls({ override }: Props) {
  const isPaused = !!override?.paused;
  const pauseMut = usePause();
  const clearMut = useClearPause();
  const [busy, setBusy] = useState(false);

  async function doPause(idx: number) {
    setBusy(true);
    try {
      let seconds: number | null;
      if (idx === 3) seconds = secondsUntilMidnight();
      else if (idx === 4) seconds = null;
      else seconds = PRESETS[idx].seconds;
      await pauseMut.mutateAsync(seconds);
    } finally {
      setBusy(false);
    }
  }

  async function doResume() {
    setBusy(true);
    try {
      await clearMut.mutateAsync();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-3 rounded-xl border border-slate-800 bg-slate-900 p-4">
      <div className="flex items-center justify-between">
        <h2 className="font-semibold">Pause rule updates</h2>
        {isPaused ? (
          <span className="rounded-full bg-amber-500/20 px-2 py-0.5 text-xs text-amber-300">
            Paused{override?.expires_ts ? ` · resumes ${new Date(override.expires_ts * 1000).toLocaleTimeString()}` : " · no limit"}
          </span>
        ) : (
          <span className="rounded-full bg-emerald-500/20 px-2 py-0.5 text-xs text-emerald-300">Active</span>
        )}
      </div>

      {isPaused ? (
        <button
          type="button"
          disabled={busy}
          onClick={doResume}
          className="w-full rounded-md bg-brand px-3 py-2 text-sm font-medium text-slate-950 hover:bg-amber-400 disabled:opacity-50"
        >
          Resume
        </button>
      ) : (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
          {PRESETS.map((p, i) => (
            <button
              key={p.label}
              type="button"
              disabled={busy}
              onClick={() => doPause(i)}
              className="rounded-md border border-slate-700 bg-slate-800 px-2 py-2 text-sm hover:border-slate-500 disabled:opacity-50"
            >
              {p.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
